import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import {memo, useCallback} from 'react'
import {graphql, useFragment} from 'react-relay'
import {useLocation} from 'react-router-dom'

import {useSetQueryParams} from '../../../../../hooks/routes'
import type {QueryParams} from '../../../../../utils/queryParams'
import {getFilterQueryParams} from '../BuildTestsTab.utils'

import type {TestsFilterCountsFragment$key} from './__generated__/TestsFilterCountsFragment.graphql'
import TestScopeBreadcrumbs from './TestScopeBreadcrumbs/TestScopeBreadcrumbs'
import TestsSearch from './TestsSearch/TestsSearch'
import TestsStatusFilter from './TestsStatusFilter/TestsStatusFilter'
import {TestFilterStatus, getStatusQueryParam} from './TestsStatusFilter/TestsStatusFilter.utils'
import TestsViewSelect from './TestsViewSelect/TestsViewSelect'

import styles from './TestsFilter.css'

const countsFragment = graphql`
  fragment TestsFilterCountsFragment on TestCounters {
    all
    ignored
    success
    muted
    failed
    newFailed
  }
`

type Props = {
  countsKey: TestsFilterCountsFragment$key | null
}

function TestsFilter({countsKey}: Props) {
  const location = useLocation()
  const setQueryParams = useSetQueryParams()
  const {name, onlyNew, withoutInvestigator} = getFilterQueryParams(location)
  const counts = useFragment(countsFragment, countsKey)
  const all = counts?.all ?? 0
  const ignored = counts?.ignored ?? 0
  const success = counts?.success ?? 0
  const muted = counts?.muted ?? 0
  const failed = counts?.failed ?? 0
  const newFailed = counts?.newFailed ?? 0
  const status = getStatusQueryParam(location)

  const onChangeQueryParams = useCallback(
    (newParams: QueryParams) => setQueryParams({...newParams, expandedTest: null, page: null}),
    [setQueryParams],
  )
  const onChangeStatus = useCallback(
    (newStatus: TestFilterStatus) => onChangeQueryParams({status: newStatus, onlyNew: null}),
    [onChangeQueryParams],
  )
  const onChangeName = useCallback(
    (value: string) => onChangeQueryParams({name: !value ? null : value}),
    [onChangeQueryParams],
  )
  const onChangeOnlyNew = useCallback(
    () => onChangeQueryParams({onlyNew: onlyNew ? null : 'true'}),
    [onChangeQueryParams, onlyNew],
  )
  const onChangeCurrentlyInvestigated = useCallback(
    () => onChangeQueryParams({withoutInvestigator: withoutInvestigator ? null : 'true'}),
    [onChangeQueryParams, withoutInvestigator],
  )

  return all > 0 ? (
    <div className={styles.filter} data-test-build-tests={'filter-wrapper'}>
      <div className={styles.firstFiltersLine}>
        <TestsViewSelect className={styles.viewSelect} onChangeQueryParams={onChangeQueryParams} />
        <TestsStatusFilter
          className={styles.statusSelect}
          status={status}
          all={all}
          success={success}
          failed={failed}
          ignored={ignored}
          muted={muted}
          onChangeStatus={onChangeStatus}
        />
      </div>
      <div className={styles.seccondFiltersLine}>
        <TestsSearch value={name} onChange={onChangeName} />
        <div className={styles.checkboxSection}>
          <div className={styles.filterWithoutInvestigator}>
            <Checkbox checked={withoutInvestigator} onChange={onChangeCurrentlyInvestigated}>
              <span>{'Without investigator'}</span>
            </Checkbox>
          </div>
          {newFailed > 0 && status === TestFilterStatus.FAILED ? (
            <div className={styles.filterOnlyNew}>
              <Checkbox checked={onlyNew} onChange={onChangeOnlyNew}>
                <span>{'Only new'}</span>
                <span className={styles.count} data-test-build-tests={'new-failed-label'}>
                  {` ${newFailed}`}
                </span>
              </Checkbox>
            </div>
          ) : null}
        </div>
      </div>
      <TestScopeBreadcrumbs className={styles.scopeFilter} />
    </div>
  ) : null
}

export default memo<Props>(TestsFilter)
